'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@kit/ui/sheet';
import { Trans } from '@kit/ui/trans';

import {
  ListingFacets,
  type CategoryGroup,
  type FacetCounts,
} from './listing-filters';

interface ListingFiltersDrawerProps {
  groups: ReadonlyArray<CategoryGroup>;
  colors: ReadonlyArray<string>;
  sizes: ReadonlyArray<string>;
  counts: FacetCounts | null;
  /** Result count for the "Show N results" apply button. */
  resultCount: number | null;
}

// Facet params that count toward the "active filters" badge (search excluded —
// it has its own visible input).
const FACET_PARAMS = [
  'category',
  'condition',
  'brand',
  'color',
  'size',
  'priceMin',
  'priceMax',
  'near',
  'delivery',
] as const;

/**
 * Mobile filter entry point. Per the research pass: on mobile the sidebar
 * collapses to a sticky "Filters" button that opens a bottom-sheet drawer
 * holding the same `ListingFacets`, with a prominent "Show N results" button
 * to dismiss. Facets still write the URL in real time (the sheet covers the
 * grid, so there's no disorienting mid-interaction reflow); "Show results"
 * simply closes the sheet over the already-updated grid.
 *
 * Hidden on `lg+` where the persistent sidebar takes over.
 */
export function ListingFiltersDrawer({
  groups,
  colors,
  sizes,
  counts,
  resultCount,
}: ListingFiltersDrawerProps) {
  const [open, setOpen] = useState(false);
  const searchParams = useSearchParams();
  const { t } = useTranslation();

  const activeCount = FACET_PARAMS.reduce(
    (n, key) => (searchParams.get(key) ? n + 1 : n),
    0,
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2 lg:hidden">
          <SlidersHorizontal className="size-4" aria-hidden />
          <Trans i18nKey="common:teamAvailability.filters.title" />
          {activeCount > 0 && (
            <span className="bg-primary text-primary-foreground ml-0.5 inline-flex size-5 items-center justify-center rounded-full text-xs font-semibold">
              {activeCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="max-h-[85dvh] overflow-y-auto rounded-t-lg"
      >
        <SheetHeader className="text-left">
          <SheetTitle>
            <Trans i18nKey="common:teamAvailability.filters.title" />
          </SheetTitle>
        </SheetHeader>
        <div className="py-4">
          <ListingFacets
            groups={groups}
            colors={colors}
            sizes={sizes}
            counts={counts}
          />
        </div>
        <SheetFooter>
          <Button className="w-full" onClick={() => setOpen(false)}>
            {resultCount === null
              ? t('explore:marketplace.showResults')
              : t('explore:marketplace.showResultsCount', {
                  count: resultCount,
                })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
